import React, { useState, useRef, useCallback, useEffect } from "react";
import TappableArabic from "./TappableArabic";
import { transcribeSpeech, getConversationReply, synthesizeSpeech, getRolePlaySession, saveRolePlaySession, deleteRolePlaySession, getDailyUsage, convertFreeChatToTopic, markRolePlayComplete, saveWordBankSentence } from "../api";
import { hasSpeechContent, isWhisperHallucination } from "../utils/speechGuards";

/**
 * ProgressRing — small circular progress indicator used in the chat control bar.
 * `fraction` (0–1) fills a gold ring clockwise from the top; `danger` recolours
 * it to the rose accent when a limit is reached.
 */
function ProgressRing({ fraction = 0, danger = false, size = 44 }) {
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, fraction));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(0,0,0,0.30)" strokeWidth={3} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={danger ? "var(--tag-rose)" : "var(--gold)"}
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - clamped)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
    </svg>
  );
}

// Delimiters must match the backend constants in wasl/core/views.py
const SECTION_DELIMITER = "~~~";       // separates feedback from scene within one language block
const TRANSLATION_DELIMITER = "|||";   // separates the Arabic block from the English block

// Initial greeting shown at the start of every free chat session asking the student
// what topic they would like to practice.  Stored in history so the AI has context
// when the student's first response is sent to the backend.
const FREE_CHAT_INITIAL_GREETING = {
  role: "assistant",
  content: "مَا الْمَوْضُوعُ الَّذِي تُرِيدُ التَّحَدُّثَ عَنْهُ الْيَوْمَ؟",
  translation: "What topic would you like to talk about today?",
};

/**
 * ConversationPanel — Role-play Arabic practice driven by the topic's scenario.
 *
 * Flow:
 *  1. On mount, AI introduces the role play and says scenario turn[0].
 *  2. Student speaks (expected response ≈ turn[1]).
 *  3. AI gives brief feedback (correct / gentle correction) then says turn[2].
 *  4. Continues alternating until the scenario ends.
 *  5. After the student's final response the AI gives an overall summary.
 *
 * Turn-index protocol (sent to backend):
 *   turn_index = 0, user_message = ""  → init (AI says turn[0])
 *   turn_index = N, user_message = X   → evaluate X vs turn[N], AI says turn[N+1]
 *
 * Frontend tracks `studentTurnIdx` (next student turn = 1, 3, 5, …).
 * Session is complete when studentTurnIdx >= totalTurns.
 *
 * Stale-closure fix: `historyRef` and `studentTurnIdxRef` always mirror the
 * corresponding state so that processAudio — which is captured inside the
 * long-lived startRecording useCallback closure — always reads the latest
 * values rather than the stale ones from when startRecording was created.
 *
 * Each AI message is stored as { role, content, translation } so the UI can
 * display a toggleable English translation beneath the Arabic bubble.
 *
 * STT provider is admin-only; non-admins see no provider information.
 */

export default function ConversationPanel({ topic, isAdmin, fullScreen = false, freeChat = false, onConvertedToTopic, onRolePlayCompleted }) {
  // --- Usage limits & daily tracking --------------------------------------
  const [limits, setLimits] = useState({ max_daily_roleplay: 3, free_chat_minutes: 10, max_response_seconds: 30 });
  const [dailyUsage, setDailyUsage] = useState({ roleplay_count: 0, free_chat_seconds: 0 });
  // Countdown timer for recording auto-stop
  const [recordingSecsLeft, setRecordingSecsLeft] = useState(null);
  const countdownRef = useRef(null);
  // Free-chat time tracking — measured from actual audio, not wall-clock
  // freeChatUsedRef: total seconds used this session (for display + limit check)
  // freeChatDeltaRef: seconds accumulated since last API send (sent as elapsed_seconds, then reset)
  // recordingStartTimeRef: marks when current recording began
  const freeChatUsedRef = useRef(0);   // initialised from server on mount
  const freeChatDeltaRef = useRef(0);  // delta to send in next request
  const recordingStartTimeRef = useRef(null);
  const [freeChatSecsElapsed, setFreeChatSecsElapsed] = useState(0);

  // --- Conversation state -------------------------------------------------
  // Each message: { role: "user"|"assistant", content: "<Arabic>", translation: "<English>",
  //                 feedbackContent?: "<Arabic feedback>", roleplayContent?: "<Arabic roleplay>",
  //                 feedbackTranslation?: "<EN feedback>", roleplayTranslation?: "<EN roleplay>",
  //                 suggestedResponse?: "<AI-suggested Arabic student reply>",
  //                 suggestedTranslation?: "<English of suggested reply>" }
  // feedbackContent / roleplayContent are present only on evaluation-turn AI messages where
  // the backend split the response into a teacher-feedback sentence and an in-character line.
  // suggestedResponse / suggestedTranslation are present only on free-chat AI messages.
  const [history, setHistory] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // --- Translation toggle -------------------------------------------------
  const [showTranslation, setShowTranslation] = useState(false);

  // --- Suggested response visibility (per message index) ------------------
  // Tracks which free-chat AI messages have their suggested response expanded.
  const [expandedSuggestions, setExpandedSuggestions] = useState(new Set());

  const toggleSuggestion = (idx) => {
    setExpandedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // --- Per-message translation reveal -------------------------------------
  // Individual "Translate" actions reveal a single message's English even when
  // the global EN toggle is off. A message's translation shows when either the
  // global toggle is on or that message index is in this set.
  const [expandedTranslations, setExpandedTranslations] = useState(new Set());
  const toggleTranslation = (idx) => {
    setExpandedTranslations((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // --- Save phrase to word bank -------------------------------------------
  // "Save" on a tutor bubble stores the Arabic + translation as a word-bank
  // sentence. savedMessages maps message index → "saving" | "saved" | "error".
  const [savedMessages, setSavedMessages] = useState({});
  const handleSavePhrase = async (idx, msg) => {
    const arabic = (msg.roleplayContent || msg.content || "").trim();
    if (!arabic || savedMessages[idx] === "saving" || savedMessages[idx] === "saved") return;
    const translation = (msg.roleplayTranslation || msg.translation || "").trim();
    setSavedMessages((prev) => ({ ...prev, [idx]: "saving" }));
    try {
      await saveWordBankSentence({ arabic, translation });
      setSavedMessages((prev) => ({ ...prev, [idx]: "saved" }));
    } catch {
      setSavedMessages((prev) => ({ ...prev, [idx]: "error" }));
    }
  };

  // --- Convert-to-topic state (admin + free chat only) --------------------
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertTopicName, setConvertTopicName] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);
  const [convertResult, setConvertResult] = useState(null); // { topic_id, topic_name, ... }

  // --- Tutor mode toggle (free chat only) ---------------------------------
  // When enabled, AI acts as a focused Arabic tutor using the student's
  // completed topics and known vocabulary. Toggling resets the conversation.
  const [tutorMode, setTutorMode] = useState(false);
  // Ref so processAudio always reads the current value from inside a closure.
  const tutorModeRef = useRef(false);

  // Keep tutorModeRef in sync with tutorMode state so processAudio — which is
  // captured inside a long-lived startRecording closure — always reads the
  // current mode rather than the stale value from when startRecording was created.
  useEffect(() => {
    tutorModeRef.current = tutorMode;
  }, [tutorMode]);

  // --- Grammar focus (admin + free chat only) ----------------------------------
  // When a grammar rule is selected, an instruction is injected into the AI prompt
  // and the grammar metadata is persisted when the chat is converted to a topic.
  const [grammarRule, setGrammarRule] = useState("");
  // Ref so processAudio/startSession always read the latest value from stale closures.
  const grammarRuleRef = useRef("");

  useEffect(() => {
    grammarRuleRef.current = grammarRule;
  }, [grammarRule]);

  // --- Scenario / role-play state -----------------------------------------
  // studentTurnIdx: the scenario turn index the student is expected to say next.
  // Starts at 1 (after AI says turn[0]).
  // Increments by 2 each round (student does odd turns; AI does even turns).
  // Session complete when studentTurnIdx >= totalTurns.
  const [studentTurnIdx, setStudentTurnIdx] = useState(1);
  const [sessionComplete, setSessionComplete] = useState(false);
  // sessionStarted: false until user explicitly clicks "Start Role Play"
  const [sessionStarted, setSessionStarted] = useState(false);
  // showHint: user-requested hint showing what to say next
  const [showHint, setShowHint] = useState(false);

  // --- Refs ---------------------------------------------------------------
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioElRef = useRef(null);
  const currentBlobUrlRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const panelBottomRef = useRef(null);
  // Set to true during unmount so onstop skips processAudio
  const isUnmountingRef = useRef(false);
  // Duration of last recording — committed to freeChatUsedRef only if speech is valid
  const pendingFreeChatSecsRef = useRef(0);
  // Ref for free_chat_minutes — always current even inside stale startRecording closure
  const freeChatMinutesRef = useRef(limits.free_chat_minutes);

  // Refs that mirror the corresponding state so that processAudio — which is
  // captured inside a long-lived startRecording closure — always reads the
  // latest values rather than stale ones from the initial render.
  const historyRef = useRef([]);
  const studentTurnIdxRef = useRef(1);

  // --- Derived scenario data ----------------------------------------------
  const scenarioTurns = (topic.scenario_turns || []).map((t) => ({
    order: t.order,
    speaker: t.speaker,
    arabic: t.arabic,
    translation: t.translation,
  }));
  const totalTurns = scenarioTurns.length;

  const topicId = topic.id || null;

  // Known Arabic word texts (steers AI vocabulary toward what student knows)
  const knownWordIds = new Set(topic.known_word_ids || []);
  const knownWords = (topic.words || [])
    .filter((w) => knownWordIds.has(w.id))
    .map((w) => w.text);

  // -------------------------------------------------------------------------
  // Load daily usage + limits on mount
  useEffect(() => {
    getDailyUsage()
      .then((data) => {
        setDailyUsage({ roleplay_count: data.roleplay_count, free_chat_seconds: data.free_chat_seconds });
        setLimits(data.limits);
        // Initialise free-chat display from server so it persists across sessions
        if (freeChat) {
          freeChatUsedRef.current = data.free_chat_seconds || 0;
          setFreeChatSecsElapsed(data.free_chat_seconds || 0);
        }
      })
      .catch(() => {});
  }, [freeChat]);

  // Keep freeChatMinutesRef in sync so startRecording always reads the latest limit
  useEffect(() => {
    freeChatMinutesRef.current = limits.free_chat_minutes;
  }, [limits.free_chat_minutes]);

  // -------------------------------------------------------------------------
  // Free chat: show initial greeting asking for the topic on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!freeChat) return;
    historyRef.current = [FREE_CHAT_INITIAL_GREETING];
    setHistory([FREE_CHAT_INITIAL_GREETING]);
    setSessionStarted(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Audio helpers
  // -------------------------------------------------------------------------
  const stopCurrentAudio = useCallback(() => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.src = "";
    }
    if (currentBlobUrlRef.current) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
  }, []);

  // Tutor mode toggle handler — defined here so stopCurrentAudio is in scope.
  const handleTutorModeChange = (checked) => {
    setTutorMode(checked);
    // Reset conversation so the AI starts fresh with the new mode
    stopCurrentAudio();
    if (freeChat) {
      // Re-show initial topic greeting so the student picks a new conversation subject
      historyRef.current = [FREE_CHAT_INITIAL_GREETING];
      setHistory([FREE_CHAT_INITIAL_GREETING]);
      setSessionStarted(true);
    } else {
      historyRef.current = [];
      setHistory([]);
      setSessionStarted(false);
    }
    studentTurnIdxRef.current = 1;
    setStudentTurnIdx(1);
    setSessionComplete(false);
    setError(null);
  };

  const ensureAudioUnlocked = useCallback(() => {
    if (audioElRef.current) return;
    const audio = new Audio();
    audio.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
    audio.play().catch(() => {});
    audioElRef.current = audio;
  }, []);

  const playAudioBase64 = useCallback((b64, onDuration) => {
    stopCurrentAudio();
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: "audio/mp3" });
    const url = URL.createObjectURL(blob);
    currentBlobUrlRef.current = url;
    const audio = audioElRef.current || new Audio();
    audioElRef.current = audio;
    audio.src = url;
    audio.onloadedmetadata = () => {
      if (onDuration && audio.duration && isFinite(audio.duration)) {
        onDuration(audio.duration);
      }
    };
    audio.onended = () => { URL.revokeObjectURL(url); currentBlobUrlRef.current = null; };
    audio.onerror = () => { URL.revokeObjectURL(url); currentBlobUrlRef.current = null; };
    audio.load();
    audio.play().catch(() => { URL.revokeObjectURL(url); currentBlobUrlRef.current = null; });
  }, [stopCurrentAudio]);

  // -------------------------------------------------------------------------
  // Session management
  // -------------------------------------------------------------------------

  /**
   * Start (or restart) the role-play session.
   * Sends turn_index=0 with empty user_message so the AI introduces the role play
   * and says the first scenario line.
   */
  const startSession = useCallback(async () => {
    if (isProcessing) return;

    // Check roleplay daily limit for non-admin users (free-chat has no scenario)
    if (!isAdmin && totalTurns > 0 && dailyUsage.roleplay_count >= limits.max_daily_roleplay) {
      setError(`You've used all ${limits.max_daily_roleplay} roleplay sessions for today. Come back tomorrow!`);
      return;
    }

    stopCurrentAudio();
    historyRef.current = [];
    studentTurnIdxRef.current = 1;
    setHistory([]);
    setStudentTurnIdx(1);
    setSessionComplete(false);
    setError(null);

    if (totalTurns === 0 && !freeChat) return;

    setIsProcessing(true);
    try {
      const result = await getConversationReply(
        topic.name || "",
        topic.description || "",
        [],       // no history for init
        "",       // empty user_message → init mode
        scenarioTurns,
        0,        // turn_index = 0 → AI says turn[0]
        knownWords,
        0,        // elapsed_seconds = 0 for init
        tutorModeRef.current,
        grammarRuleRef.current
      );
    const aiMsg = {
      role: "assistant",
      content: result.text || "",
      translation: result.translation || "",
      feedbackContent: result.feedback_arabic || "",
      roleplayContent: result.roleplay_arabic || "",
      feedbackTranslation: result.feedback_translation || "",
      roleplayTranslation: result.roleplay_translation || "",
      suggestedResponse: result.suggested_student_arabic || "",
      suggestedTranslation: result.suggested_student_translation || "",
      isInit: true,
    };
      historyRef.current = [aiMsg];
      setHistory([aiMsg]);
      if (result.audio_content) playAudioBase64(result.audio_content);
      // Bump local daily count so UI reflects new count without a refetch
      if (!isAdmin && totalTurns > 0) {
        setDailyUsage((prev) => ({ ...prev, roleplay_count: prev.roleplay_count + 1 }));
      }
    } catch {
      setError("Could not start the role play. Please try again.");
    } finally {
      setIsProcessing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, totalTurns, isProcessing, isAdmin, dailyUsage, limits, playAudioBase64, stopCurrentAudio]);

  // Load saved session from the backend API on mount (keyed by topic.id)
  useEffect(() => {
    if (!topicId) return;
    getRolePlaySession(topicId)
      .then((data) => {
        if (data && Array.isArray(data.history) && data.history.length > 0) {
          historyRef.current = data.history;
          studentTurnIdxRef.current = data.student_turn_idx;
          setHistory(data.history);
          setStudentTurnIdx(data.student_turn_idx);
          setSessionComplete(data.session_complete === true);
          setSessionStarted(true);
        }
      })
      .catch(() => {
        // Session load failed silently — start fresh
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist session to the backend whenever history changes (debounced 1s)
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!topicId || history.length === 0) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveRolePlaySession(topicId, { history, studentTurnIdx, sessionComplete }).catch(() => {});
    }, 1000);
    return () => clearTimeout(saveTimerRef.current);
  }, [history, studentTurnIdx, sessionComplete, topicId]);

  // When a *scenario* role-play session reaches completion, automatically
  // mark the role-play progression step complete on the backend. Idempotent
  // and gated by the backend's order enforcement (will 409 if earlier steps
  // aren't done — we swallow that error). Free-chat sessions are not part of
  // the guided progression and are skipped.
  // Keyed per topic so navigating between topics re-arms the guard.
  const roleplayCompleteSentRef = useRef({ topicId: null, sent: false });
  useEffect(() => {
    // Reset the guard whenever the active topic changes.
    if (roleplayCompleteSentRef.current.topicId !== topicId) {
      roleplayCompleteSentRef.current = { topicId, sent: false };
    }
    if (freeChat) return;
    if (!topicId) return;
    if (!sessionComplete) return;
    if (roleplayCompleteSentRef.current.sent) return;
    roleplayCompleteSentRef.current.sent = true;
    markRolePlayComplete(topicId)
      .then((res) => {
        // Notify the host with the confirmed progression so the guided journey
        // updates immediately (avoids a race where exit refreshes the topic
        // before the backend has committed roleplay_completed).
        if (onRolePlayCompleted) onRolePlayCompleted(res?.progression || null);
      })
      .catch(() => {
        // Allow a retry on next render (e.g. user finished prerequisites later)
        roleplayCompleteSentRef.current.sent = false;
      });
  }, [sessionComplete, topicId, freeChat, onRolePlayCompleted]);

  // Scroll to latest message
  useEffect(() => {
    if (history.length === 0 && !isProcessing) return;
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    panelBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [history, isProcessing]);

  useEffect(() => {
    panelBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // Release mic + stop timers when the component unmounts (e.g. user exits mid-recording)
  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (countdownRef.current) clearInterval(countdownRef.current);
      stopCurrentAudio();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Recording
  // -------------------------------------------------------------------------
  const startRecording = useCallback(async () => {
    setError(null);
    stopCurrentAudio();
    ensureAudioUnlocked();

    // Free-chat daily limit check — use ref so we always get the latest limit value
    // even if this startRecording closure was created before the limit was changed
    if (freeChat && !isAdmin) {
      const currentLimit = freeChatMinutesRef.current || 10;
      const maxSecs = currentLimit * 60;
      if (freeChatUsedRef.current >= maxSecs) {
        setError(`You've used your ${currentLimit}-minute free chat for today. Come back tomorrow!`);
        return;
      }
    }

    // Auto-start session on first mic tap in free-chat mode
    if (freeChat && !sessionStarted) {
      setSessionStarted(true);
    }

    if (!window.isSecureContext || !navigator.mediaDevices) {
      setError("Microphone requires a secure connection (HTTPS). Please open the app via https:// or on localhost.");
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Microphone access was denied. Please allow microphone permission in your browser settings and try again.");
      } else if (err.name === "NotFoundError") {
        setError("No microphone found. Please connect a microphone and try again.");
      } else {
        setError(`Could not access microphone: ${err.message}`);
      }
      return;
    }

    audioChunksRef.current = [];
    const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""]
      .find((t) => t === "" || MediaRecorder.isTypeSupported(t));
    const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      if (isUnmountingRef.current) return; // component unmounting — discard audio
      const type = mediaRecorder.mimeType || "audio/webm";
      const blob = new Blob(audioChunksRef.current, { type });
      try {
        await processAudio(blob);
      } catch {
        setError("Something went wrong processing your audio. Please try again.");
        setIsProcessing(false);
      }
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);

    // Track recording start for free-chat duration measurement
    if (freeChat) recordingStartTimeRef.current = Date.now();

    // Auto-stop countdown
    const maxSecs = limits.max_response_seconds || 30;
    setRecordingSecsLeft(maxSecs);
    let remaining = maxSecs;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setRecordingSecsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        // Store recording duration — committed only if speech passes validation
        if (freeChat && recordingStartTimeRef.current) {
          pendingFreeChatSecsRef.current = (Date.now() - recordingStartTimeRef.current) / 1000;
          recordingStartTimeRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
      }
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopCurrentAudio, ensureAudioUnlocked, limits.max_response_seconds]);

  const stopRecording = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setRecordingSecsLeft(null);
    // Store recording duration — committed only if speech passes validation
    if (recordingStartTimeRef.current) {
      pendingFreeChatSecsRef.current = (Date.now() - recordingStartTimeRef.current) / 1000;
      recordingStartTimeRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  // -------------------------------------------------------------------------
  // Process one round: transcribe → AI reply → advance turn
  // -------------------------------------------------------------------------

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    setError(null);

    // 0. Check for silence before paying for STT
    const hasSpeech = await hasSpeechContent(audioBlob);
    if (!hasSpeech) {
      pendingFreeChatSecsRef.current = 0; // discard — no real speech
      setError("No speech detected. Please speak clearly and try again.");
      setIsProcessing(false);
      return;
    }

    // 1. Transcribe speech
    let userText = "";
    try {
      const transcribeResult = await transcribeSpeech(audioBlob);
      userText = (transcribeResult.text || "").trim();
    } catch {
      setError("Could not transcribe audio. Please try again.");
      setIsProcessing(false);
      return;
    }

    if (!userText || isWhisperHallucination(userText)) {
      pendingFreeChatSecsRef.current = 0; // discard — hallucination, not real speech
      setError("No speech detected. Please speak clearly and try again.");
      setIsProcessing(false);
      return;
    }

    // Valid speech — commit recording duration to free-chat timer
    if (freeChat && pendingFreeChatSecsRef.current > 0) {
      freeChatUsedRef.current += pendingFreeChatSecsRef.current;
      freeChatDeltaRef.current += pendingFreeChatSecsRef.current;
      setFreeChatSecsElapsed(freeChatUsedRef.current);
      pendingFreeChatSecsRef.current = 0;
    }

    // Read the latest state values from refs so this function always has
    // up-to-date history and turn index even when called from a stale closure
    // (i.e. the onstop handler set up inside the startRecording useCallback).
    const currentHistory = historyRef.current;
    const currentStudentTurnIdx = studentTurnIdxRef.current;

    // Show user message immediately (no translation for user messages)
    const userMsg = { role: "user", content: userText, translation: "" };
    const updatedHistory = [...currentHistory, userMsg];
    historyRef.current = updatedHistory;
    setHistory(updatedHistory);

    // 2. Get AI feedback + next role-play line.
    // Send the history BEFORE this user message; the backend receives the
    // user message separately via user_message.
    // Reconstruct the original delimited format for split AI messages so the
    // model sees its expected output format in the conversation history and
    // continues using the ~~~ / ||| delimiters consistently throughout the session.
    const historyForApi = currentHistory.map((m) => {
      if (m.role === "assistant" && m.feedbackContent && m.roleplayContent) {
        const arabicPart = `${m.feedbackContent}\n${SECTION_DELIMITER}\n${m.roleplayContent}`;
        const translationPart = (m.feedbackTranslation && m.roleplayTranslation)
          ? `${m.feedbackTranslation}\n${SECTION_DELIMITER}\n${m.roleplayTranslation}`
          : (m.translation || "");
        return { role: m.role, content: `${arabicPart}\n${TRANSLATION_DELIMITER}\n${translationPart}` };
      }
      return { role: m.role, content: m.content };
    });

    let replyText = "";
    let replyTranslation = "";
    let audiob64 = "";
    let replyResult = null;
    try {
      replyResult = await getConversationReply(
        topic.name || "",
        topic.description || "",
        historyForApi,
        userText,
        scenarioTurns,
        currentStudentTurnIdx,   // backend evaluates student vs turn[currentStudentTurnIdx]
        knownWords,
        freeChatDeltaRef.current,  // seconds since last request (student speech duration)
        tutorModeRef.current,
        grammarRuleRef.current
      );
      freeChatDeltaRef.current = 0; // reset after successful send
      replyText = replyResult.text || "";
      replyTranslation = replyResult.translation || "";
      audiob64 = replyResult.audio_content || "";
    } catch (err) {
      if (err.status === 429) {
        setError(err.message || "Daily limit reached. Please come back tomorrow.");
      } else {
        setError("Could not get AI reply. Please try again.");
      }
      setHistory(updatedHistory);
      setIsProcessing(false);
      return;
    }

    const aiMsg = {
      role: "assistant",
      content: replyText,
      translation: replyTranslation,
      feedbackContent: replyResult.feedback_arabic || "",
      roleplayContent: replyResult.roleplay_arabic || "",
      feedbackTranslation: replyResult.feedback_translation || "",
      roleplayTranslation: replyResult.roleplay_translation || "",
      suggestedResponse: replyResult.suggested_student_arabic || "",
      suggestedTranslation: replyResult.suggested_student_translation || "",
    };
    const newHistory = [...updatedHistory, aiMsg];
    historyRef.current = newHistory;
    setHistory(newHistory);

    // 3. Advance to next student turn (skip AI's next turn: +2)
    const nextStudentTurn = currentStudentTurnIdx + 2;
    studentTurnIdxRef.current = nextStudentTurn;
    setStudentTurnIdx(nextStudentTurn);
    if (totalTurns > 0 && nextStudentTurn >= totalTurns) {
      setSessionComplete(true);
    }

    setIsProcessing(false);

    // 4. Play AI audio
    if (audiob64) {
      playAudioBase64(audiob64);
    } else if (replyText) {
      try {
        const ttsResult = await synthesizeSpeech(replyText, "المعلمة");
        if (ttsResult.audio_content) playAudioBase64(ttsResult.audio_content);
      } catch {
        // Audio failed silently — text is still shown
      }
    }
  };

  const handleRestart = () => {
    if (topicId) {
      deleteRolePlaySession(topicId).catch(() => {});
    }
    stopCurrentAudio();
    studentTurnIdxRef.current = 1;
    setStudentTurnIdx(1);
    setSessionComplete(false);
    setError(null);
    setConvertResult(null);
    setConvertError(null);
    if (freeChat) {
      // Re-show initial topic greeting for a fresh free chat session
      historyRef.current = [FREE_CHAT_INITIAL_GREETING];
      setHistory([FREE_CHAT_INITIAL_GREETING]);
      setSessionStarted(true);
    } else {
      historyRef.current = [];
      setHistory([]);
      setSessionStarted(false);
    }
  };

  // End the free chat session (marks it complete so the convert option appears).
  const handleEndChat = () => {
    stopCurrentAudio();
    setSessionComplete(true);
  };

  // Convert the current free chat history into a topic (admin only).
  const handleConvertToTopic = async () => {
    const name = convertTopicName.trim();
    if (!name) {
      setConvertError("Please enter a topic name.");
      return;
    }
    setIsConverting(true);
    setConvertError(null);
    try {
      const result = await convertFreeChatToTopic(historyRef.current, name, grammarRuleRef.current);
      setConvertResult(result);
      setShowConvertModal(false);
    } catch (err) {
      setConvertError(err.message || "Conversion failed. Please try again.");
    } finally {
      setIsConverting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  // Number of student turns completed so far
  const studentTurnsCompleted = Math.floor(history.filter((m) => m.role === "user").length);
  // Total student turns in the scenario (odd indices: 1, 3, 5 …)
  const totalStudentTurns = Math.ceil(totalTurns / 2);
  const progressLabel = totalStudentTurns > 0
    ? `${Math.min(studentTurnsCompleted, totalStudentTurns)} / ${totalStudentTurns}`
    : null;

  return (
    <>
    <div className={fullScreen
      ? "flex flex-col h-full surface-glass rounded-xl shadow-soft overflow-hidden"
      : "surface-glass rounded-xl shadow-soft overflow-hidden"
    }>

      {/* Control bar — status ring + session controls.
          flex-wrap so the controls drop to a second line instead of overlapping
          the status block on narrow (mobile) screens. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3" style={{ borderBottom: "1px solid var(--panel-line)" }}>
        {/* Left: progress ring + status */}
        <div className="flex items-center gap-3 min-w-0">
          {freeChat ? (
            (() => {
              const maxSecs = (limits.free_chat_minutes || 10) * 60;
              const remaining = Math.max(0, Math.floor(maxSecs - freeChatSecsElapsed));
              const mins = Math.floor(remaining / 60);
              const secs = remaining % 60;
              const frac = maxSecs > 0 ? freeChatSecsElapsed / maxSecs : 0;
              const exhausted = remaining <= 0;
              return (
                <>
                  <ProgressRing fraction={frac} danger={exhausted} />
                  <div className="leading-tight">
                    <div className="text-lg font-semibold tabular-nums" style={{ color: exhausted ? "var(--tag-rose)" : "var(--parchment)" }}>
                      {mins}:{String(secs).padStart(2, "0")}
                    </div>
                    <div className="text-xs" style={{ color: "var(--parchment-dim)" }}>
                      {exhausted ? "free chat used up" : `of ${limits.free_chat_minutes} min free chat`}
                    </div>
                  </div>
                </>
              );
            })()
          ) : (
            <>
              <ProgressRing fraction={totalStudentTurns > 0 ? studentTurnsCompleted / totalStudentTurns : 0} />
              <div className="leading-tight">
                <div className="text-lg font-semibold tabular-nums" style={{ color: "var(--parchment)" }}>
                  {progressLabel || "Role Play"}
                </div>
                <div className="text-xs" style={{ color: "var(--parchment-dim)" }}>
                  {totalStudentTurns > 0 ? "turns completed" : "conversation practice"}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right: session controls. max-w-full + flex-wrap so the buttons wrap
            within the bar instead of overflowing off the right screen edge when
            all of them (Tutor / Grammar / EN / Restart) are present on mobile. */}
        <div className="flex flex-wrap items-center justify-start gap-2 shrink-0 max-w-full">
          {/* Tutor mode toggle — free chat only */}
          {freeChat && (
            <button
              type="button"
              role="switch"
              aria-checked={tutorMode}
              onClick={() => handleTutorModeChange(!tutorMode)}
              disabled={isRecording || isProcessing}
              title="When on, the AI focuses on your learned topics and known vocabulary"
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-full transition-colors disabled:opacity-50"
              style={{ border: "1px solid var(--panel-line)", background: tutorMode ? "var(--gold-bg)" : "transparent" }}
            >
              <span className="relative inline-block w-9 h-5 rounded-full transition-colors" style={{ background: tutorMode ? "var(--gold)" : "rgba(255,255,255,0.15)" }}>
                <span
                  className="absolute top-0.5 w-4 h-4 rounded-full transition-transform"
                  style={{ left: 2, background: tutorMode ? "var(--forest)" : "var(--parchment-dim)", transform: tutorMode ? "translateX(16px)" : "none" }}
                />
              </span>
              <span className="text-xs font-medium whitespace-nowrap" style={{ color: "var(--parchment)" }}>Tutor mode</span>
            </button>
          )}

          {/* Admin-only grammar focus selector — free chat only */}
          {isAdmin && freeChat && (
            <div className="flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full min-w-0" style={{ border: "1px solid var(--panel-line)" }}>
              <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>Grammar</span>
            <select
              value={grammarRule}
              onChange={(e) => {
                const newRule = e.target.value;
                setGrammarRule(newRule);
                // Reset conversation so the AI starts fresh with the new grammar focus
                stopCurrentAudio();
                historyRef.current = [FREE_CHAT_INITIAL_GREETING];
                setHistory([FREE_CHAT_INITIAL_GREETING]);
                setSessionStarted(true);
                studentTurnIdxRef.current = 1;
                setStudentTurnIdx(1);
                setSessionComplete(false);
                setError(null);
              }}
              disabled={isRecording || isProcessing}
              title="Grammar focus (admin only) — instructs the AI to use a specific grammar pattern"
              aria-label="Grammar focus"
              className="text-xs font-medium focus:outline-none cursor-pointer min-w-0 max-w-[7rem] truncate"
              style={{ background: "transparent", border: "none", color: "var(--gold-bright)" }}
            >
              <option value="">No focus</option>
              <optgroup label="A1">
                <option value="nominal_sentence">Nominal Sentences (الجملة الاسمية)</option>
                <option value="personal_pronouns">Personal Pronouns (الضَّمائِر الشَّخْصِيَّة)</option>
                <option value="prepositions">Prepositions (حُرُوف الجَر)</option>
                <option value="definite_indefinite">Definite vs Indefinite (الْمَعْرِفَة وَالنَّكِرَة)</option>
                <option value="word_order">Basic Word Order (تَرْتِيبُ الْكَلِمَاتِ)</option>
                <option value="past_tense">Past Tense Verbs (الفِعْل الماضِي)</option>
                <option value="present_tense_intro">Present Tense (الفعل المضارع)</option>
                <option value="gender">Masculine vs Feminine (الْمُذَكَّر وَالْمُؤَنَّث)</option>
                <option value="question_words">Question Words (أَدَوَاتُ الِاسْتِفْهَام)</option>
                <option value="adjectives">Adjectives (النَّعْت)</option>
              </optgroup>
              <optgroup label="A2">
                <option value="idafa">Idafa / Possession (الْإِضَافَة)</option>
                <option value="negation">Negation (النَّفْي)</option>
                <option value="connectors">Basic Connectors (رَوَابِطُ الْجُمَل)</option>
                <option value="verb_object">Verb + Object (الفعل والمفعول به)</option>
                <option value="future_tense">Future Tense (الْمُسْتَقْبَل)</option>
                <option value="object_pronouns_basic">Verb + Object Pronouns (ضمائر المفعول المتصلة)</option>
                <option value="object_pronouns_expanded">Object Pronouns (Expanded) (الضمائر المتصلة)</option>
                <option value="adjective_agreement">Adjective Agreement (تطابق النعت)</option>
                <option value="comparatives">Comparatives (اسم التفضيل)</option>
                <option value="dual_forms">Dual Forms (الْمُثَنَّى)</option>
              </optgroup>
              <optgroup label="B1">
                <option value="broken_plurals_intro">Broken Plurals (جمع التكسير)</option>
                <option value="imperatives">Imperatives (الأمر)</option>
              </optgroup>
            </select>
            </div>
          )}

          {/* Translation toggle */}
          <button
            type="button"
            onClick={() => setShowTranslation((v) => !v)}
            title={showTranslation ? "Hide translations" : "Show translations"}
            className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full transition-colors"
            style={{
              border: "1px solid var(--panel-line)",
              background: showTranslation ? "var(--gold)" : "transparent",
              color: showTranslation ? "var(--forest)" : "var(--parchment)",
            }}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" />
            </svg>
            EN
          </button>

          {/* Restart button */}
          {sessionStarted && (freeChat ? history.length > 1 : history.length > 0) && !isProcessing && !isRecording && (
            <button
              type="button"
              onClick={handleRestart}
              title={freeChat ? "Restart chat" : "Restart role play"}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors"
              style={{ border: "1px solid var(--panel-line)", color: "var(--parchment-dim)" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--gold-bright)"; e.currentTarget.style.borderColor = "var(--gold)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--parchment-dim)"; e.currentTarget.style.borderColor = "var(--panel-line)"; }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Non-admin: sessions remaining banner (roleplay) — shown only when the
          daily limit is reached; the ring conveys progress otherwise. */}
      {!isAdmin && !freeChat && totalTurns > 0 && dailyUsage.roleplay_count >= limits.max_daily_roleplay && (
        <div className="px-4 py-1.5 text-xs flex items-center gap-1.5" style={{ borderBottom: "1px solid var(--panel-line)", background: "var(--tag-rose-bg)", color: "var(--tag-rose)" }}>
          <span>🚫 No roleplay sessions remaining today. Come back tomorrow!</span>
        </div>
      )}

      {/* Message history */}
      <div ref={messagesContainerRef} className={fullScreen ? "flex-1 overflow-y-auto px-4 py-4" : "px-4 py-4 max-h-96 overflow-y-auto"}>
        <div className="flex flex-col space-y-5">
          {totalTurns === 0 && !freeChat && !isProcessing && (
            <p className="text-sm text-center py-4" style={{ color: "var(--parchment-dim)" }}>
              No scenario available for <span className="font-medium" style={{ color: "var(--parchment)" }}>{topic.name}</span> yet.
            </p>
          )}
          {freeChat && !sessionStarted && (
            <p className="text-sm text-center py-4" style={{ color: "var(--parchment-dim)" }}>
              {tutorMode
                ? "Tutor mode on — the AI will focus on your completed topics and known vocabulary. Tap the mic to start!"
                : "Tap the mic and speak in Arabic — say anything you like!"}
            </p>
          )}
          {totalTurns > 0 && !sessionStarted && (
            <p className="text-sm text-center py-4" style={{ color: "var(--parchment-dim)" }}>
              Click <span className="font-medium" style={{ color: "var(--gold-bright)" }}>Start Role Play</span> below to begin.
            </p>
          )}
          {totalTurns > 0 && sessionStarted && history.length === 0 && !isProcessing && (
            <p className="text-sm text-center py-4" style={{ color: "var(--parchment-dim)" }}>
              Starting role play for <span className="font-medium" style={{ color: "var(--parchment)" }}>{topic.name}</span>…
            </p>
          )}
          {history.map((msg, idx) => {
            const isUser = msg.role === "user";
            const isSplit = msg.role === "assistant" && msg.feedbackContent && msg.roleplayContent;
            // Per-message translation reveal: global EN toggle OR this message expanded.
            const showTr = showTranslation || expandedTranslations.has(idx);
            const hasTranslation = !!(msg.translation || msg.feedbackTranslation || msg.roleplayTranslation);
            const saveState = savedMessages[idx];
            return (
              <div key={idx} className={`flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`}>
                {/* Speaker label (+ avatar for the tutor) */}
                <div className={`flex items-center gap-2 ${isUser ? "flex-row-reverse pr-1" : ""}`}>
                  {!isUser && (
                    <span
                      className="flex items-center justify-center w-7 h-7 rounded-lg text-base shrink-0"
                      style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)" }}
                      aria-hidden="true"
                    >
                      🕌
                    </span>
                  )}
                  <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--parchment-dim)" }}>
                    {isUser ? "You" : "Tutor"}
                  </span>
                </div>

                {/* Bubble */}
                <div
                  className={`max-w-[85%] px-4 py-3 text-base leading-relaxed ${isUser ? "rounded-2xl rounded-tr-md" : "rounded-2xl rounded-tl-md"}`}
                  style={
                    isUser
                      ? { background: "var(--forest-softer)", color: "var(--parchment)" }
                      : { background: "rgba(18, 45, 34, 0.6)", color: "var(--parchment)", borderLeft: "3px solid var(--gold)" }
                  }
                >
                  {isSplit ? (
                    /* Split view: teacher feedback / intro + in-character scene line */
                    <>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--gold-bright)" }}>
                          {msg.isInit ? "📖 Introduction" : "💬 Feedback"}
                        </p>
                        <TappableArabic text={msg.feedbackContent} className="font-arabic text-xl" style={{ color: "var(--gold-bright)" }} />
                        {showTr && msg.feedbackTranslation && (
                          <p className="mt-1 text-sm italic" style={{ color: "var(--parchment-dim)" }}>{msg.feedbackTranslation}</p>
                        )}
                      </div>
                      <div className="my-2.5 border-t" style={{ borderColor: "var(--panel-line)" }} />
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--gold-bright)" }}>🎭 Scene</p>
                        <TappableArabic text={msg.roleplayContent} className="font-arabic font-medium text-xl" />
                        {showTr && msg.roleplayTranslation && (
                          <p className="mt-1 text-sm italic" style={{ color: "var(--parchment-dim)" }}>{msg.roleplayTranslation}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    /* Plain view — greeting, final summary, or single-line messages */
                    <>
                      <TappableArabic text={msg.content} className="font-arabic text-xl" />
                      {showTr && msg.translation && (
                        <p className="mt-1.5 text-sm italic" style={{ color: isUser ? "rgba(244,238,220,0.75)" : "var(--parchment-dim)" }}>
                          {msg.translation}
                        </p>
                      )}
                    </>
                  )}

                  {/* Tutor action row — Translate / Save (+ suggested response) */}
                  {msg.role === "assistant" && (
                    <div className="mt-3 pt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5" style={{ borderTop: "1px solid var(--panel-line)" }}>
                      {hasTranslation && (
                        <button
                          type="button"
                          onClick={() => toggleTranslation(idx)}
                          className="flex items-center gap-1.5 text-xs font-medium transition-colors"
                          style={{ color: showTr ? "var(--gold-bright)" : "var(--parchment-dim)" }}
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                          </svg>
                          {showTr ? "Hide" : "Translate"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleSavePhrase(idx, msg)}
                        disabled={saveState === "saving" || saveState === "saved"}
                        className="flex items-center gap-1.5 text-xs font-medium transition-colors disabled:cursor-default"
                        style={{ color: saveState === "saved" ? "var(--tag-teal)" : saveState === "error" ? "var(--tag-rose)" : "var(--parchment-dim)" }}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={saveState === "saved" ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                        {saveState === "saved" ? "Saved" : saveState === "saving" ? "Saving…" : saveState === "error" ? "Retry" : "Save"}
                      </button>
                      {freeChat && msg.suggestedResponse && (
                        <button
                          type="button"
                          onClick={() => toggleSuggestion(idx)}
                          className="flex items-center gap-1.5 text-xs font-medium transition-colors"
                          style={{ color: expandedSuggestions.has(idx) ? "var(--gold-bright)" : "var(--parchment-dim)" }}
                        >
                          <span>💡</span>
                          <span>{expandedSuggestions.has(idx) ? "Hide suggestion" : "Suggested reply"}</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Expanded suggested student response — free chat AI messages only */}
                  {freeChat && msg.role === "assistant" && msg.suggestedResponse && expandedSuggestions.has(idx) && (
                    <div className="mt-2 px-3 py-2 rounded-lg" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid var(--panel-line)" }}>
                      <TappableArabic text={msg.suggestedResponse} className="font-arabic text-base leading-relaxed" style={{ color: "var(--gold-bright)" }} />
                      {msg.suggestedTranslation && (
                        <p className="mt-1 text-xs italic" style={{ color: "var(--parchment-dim)" }}>{msg.suggestedTranslation}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {isProcessing && (
            <div className="flex flex-col gap-1.5 items-start">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg text-base shrink-0" style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)" }} aria-hidden="true">🕌</span>
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--parchment-dim)" }}>Tutor</span>
              </div>
              <div className="px-4 py-3 rounded-2xl rounded-tl-md flex items-center gap-1.5" style={{ background: "rgba(18, 45, 34, 0.6)", borderLeft: "3px solid var(--gold)" }}>
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--gold-bright)", animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--gold-bright)", animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--gold-bright)", animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Session complete banner */}
      {sessionComplete && (
        <div className="mx-4 mb-2 space-y-2">
          <div className="px-3 py-2 rounded-lg text-xs flex items-center justify-between gap-2" style={{ background: "var(--tag-teal-bg)", border: "1px solid var(--panel-line)", color: "var(--tag-teal)" }}>
            <span>{freeChat ? "💬 Chat session ended." : "🎉 Role play complete!"}</span>
            <button
              type="button"
              onClick={handleRestart}
              className="text-xs font-semibold underline hover:no-underline"
              style={{ color: "var(--gold-bright)" }}
            >
              {freeChat ? "New Chat" : "Start Over"}
            </button>
          </div>
          {/* Convert to Topic — admin + free chat only */}
          {freeChat && isAdmin && !convertResult && (
            <div className="px-3 py-2 rounded-lg text-xs flex items-center justify-between gap-2" style={{ background: "var(--gold-bg)", border: "1px solid var(--panel-line)", color: "var(--gold-bright)" }}>
              <span>📚 Save this chat as a learning topic?</span>
              <button
                type="button"
                onClick={() => { setConvertTopicName(""); setConvertError(null); setShowConvertModal(true); }}
                className="text-xs font-semibold underline hover:no-underline shrink-0"
                style={{ color: "var(--gold-bright)" }}
              >
                Convert to Topic
              </button>
            </div>
          )}
          {/* Conversion success */}
          {convertResult && (
            <div className="px-3 py-2 rounded-lg text-xs flex items-center justify-between gap-2" style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)", color: "var(--parchment)" }}>
              <span>✅ Topic <strong>{convertResult.topic_name}</strong> created! ({convertResult.vocab_count} words, {convertResult.scenario_turns} turns)</span>
              {onConvertedToTopic && (
                <button
                  type="button"
                  onClick={() => onConvertedToTopic(convertResult.topic_id)}
                  className="text-xs font-semibold underline hover:no-underline shrink-0"
                  style={{ color: "var(--gold-bright)" }}
                >
                  View Topic
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Errors */}
      {error && (
        <p className="px-4 pb-1 text-xs" style={{ color: "var(--tag-rose)" }}>{error}</p>
      )}

      {/* "Show me what to say" hint */}
      {sessionStarted && !sessionComplete && studentTurnIdx < totalTurns && history.length > 0 && (
        <div className="mx-4 mb-2">
          <button
            type="button"
            onClick={() => setShowHint((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium transition-colors"
            style={{ color: "var(--gold-bright)" }}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {showHint ? "Hide hint" : "Show me what to say"}
          </button>
          {showHint && scenarioTurns[studentTurnIdx] && (
            <div className="mt-2 px-3 py-2 rounded-lg" style={{ background: "var(--gold-bg)", border: "1px solid var(--panel-line)" }}>
              <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--gold-bright)" }}>Your line</p>
              <TappableArabic text={scenarioTurns[studentTurnIdx].arabic} className="text-lg font-arabic leading-relaxed" style={{ color: "var(--parchment)" }} />
              {scenarioTurns[studentTurnIdx].translation && (
                <p className="mt-1 text-sm italic" style={{ color: "var(--parchment-dim)" }}>{scenarioTurns[studentTurnIdx].translation}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mic controls or Start Role Play button */}
      {!sessionComplete && (totalTurns > 0 || freeChat) && (() => {
        // Shared big-button styles for the bottom action bar.
        const primaryStyle = {
          background: "linear-gradient(180deg, var(--gold-bright), var(--gold))",
          color: "#12281E",
          boxShadow: "0 6px 22px rgba(201,162,39,0.28)",
        };
        const recordingStyle = {
          background: "linear-gradient(180deg, #D9805F, var(--tag-rose))",
          color: "#fff",
        };
        const mutedStyle = { background: "var(--forest-soft)", color: "var(--parchment-dim)" };
        const bigBtn = "flex-1 flex items-center justify-center gap-2.5 py-4 rounded-2xl text-base font-semibold select-none transition-all";

        const MicIcon = () => (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1a4 4 0 014 4v6a4 4 0 01-8 0V5a4 4 0 014-4zm0 2a2 2 0 00-2 2v6a2 2 0 004 0V5a2 2 0 00-2-2zm-7 9a1 1 0 012 0 5 5 0 0010 0 1 1 0 012 0 7 7 0 01-6 6.93V21h2a1 1 0 010 2H9a1 1 0 010-2h2v-2.07A7 7 0 015 12z" />
          </svg>
        );
        const StopButton = () => (
          <button type="button" onClick={stopRecording} className={`${bigBtn} animate-pulse`} style={recordingStyle} title="Tap to stop and send">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14" rx="2" /></svg>
            Stop &amp; Send
            {recordingSecsLeft !== null && (
              <span className="ml-1 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums" style={{ background: "rgba(0,0,0,0.25)" }}>{recordingSecsLeft}s</span>
            )}
          </button>
        );
        const ProcessingButton = () => (
          <div className={bigBtn} style={mutedStyle}>
            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Processing…
          </div>
        );

        if (freeChat) {
          const maxSecs = (limits.free_chat_minutes || 10) * 60;
          const exhausted = !isAdmin && freeChatUsedRef.current >= maxSecs;
          return (
            <div className="px-4 py-3 flex items-center gap-3" style={{ borderTop: "1px solid var(--panel-line)" }}>
              {isProcessing ? (
                <ProcessingButton />
              ) : isRecording ? (
                <StopButton />
              ) : exhausted ? (
                <div className={bigBtn} style={mutedStyle}>Daily free-chat limit reached</div>
              ) : (
                <>
                  <button type="button" onClick={startRecording} className={bigBtn} style={primaryStyle} title="Tap to start speaking">
                    <MicIcon /> Tap to Speak
                  </button>
                  {sessionStarted && history.length > 1 && (
                    <button
                      type="button"
                      onClick={handleEndChat}
                      className="dt-ghost-btn px-6 py-4 rounded-2xl text-sm font-medium select-none"
                      title="End this chat session"
                    >
                      End Chat
                    </button>
                  )}
                </>
              )}
            </div>
          );
        }

        // Role-play mode
        return (
          <div className="px-4 py-3 flex items-center gap-3" style={{ borderTop: "1px solid var(--panel-line)" }}>
            {!sessionStarted ? (
              <button
                type="button"
                onClick={() => { setSessionStarted(true); startSession(); }}
                className={bigBtn}
                style={primaryStyle}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                Start Role Play
              </button>
            ) : isProcessing ? (
              <ProcessingButton />
            ) : isRecording ? (
              <StopButton />
            ) : history.length > 0 ? (
              <button type="button" onClick={startRecording} className={bigBtn} style={primaryStyle} title="Tap to start recording">
                <MicIcon /> Tap to Speak
              </button>
            ) : null}
          </div>
        );
      })()}
    </div>
    <div ref={panelBottomRef} aria-hidden="true" />

    {/* Convert-to-topic modal (admin + free chat only) */}
    {showConvertModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4">
        <div className="w-full max-w-sm surface-glass rounded-2xl shadow-xl p-5 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-base font-display font-semibold text-ink-800">Convert Chat to Topic</h2>
              <p className="text-xs text-ink-400 mt-0.5">
                The chat will become a learning topic with vocabulary, verbs, expressions, and scenario turns.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowConvertModal(false)}
              className="text-ink-400 hover:text-ink-600 transition-colors shrink-0"
              aria-label="Close"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-ink-700" htmlFor="convert-topic-name">
              Topic Name
            </label>
            <input
              id="convert-topic-name"
              type="text"
              value={convertTopicName}
              onChange={(e) => setConvertTopicName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleConvertToTopic(); }}
              placeholder="e.g. Free Chat — Greetings"
              maxLength={200}
              className="w-full px-3 py-2 border border-parchment-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink-400"
              disabled={isConverting}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </div>

          {convertError && (
            <p className="text-xs text-red-500">{convertError}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowConvertModal(false)}
              disabled={isConverting}
              className="px-4 py-2 text-sm rounded-lg border border-parchment-300 text-ink-600 hover:bg-parchment-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConvertToTopic}
              disabled={isConverting || !convertTopicName.trim()}
              className="px-4 py-2 text-sm rounded-lg bg-gold-500 text-white font-medium hover:bg-gold-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {isConverting ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Creating…
                </>
              ) : "Create Topic"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
